import {
	Component,
	HostListener,
	Input,
	OnChanges,
	OnDestroy,
	SimpleChanges,
} from '@angular/core';
import { ToastService } from '@core/components/toasts/toasts.service';
import moment from 'moment';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { JobTitleService } from 'services/hiring/jobTitle.service';
import { JobTitleHistoryModel } from 'services/models/hiring/jobTitleHistory.model';
import { QueryParams } from 'services/models/odata/queryParams';

export interface SalaryHistorySummaryCard {
	key: 'lastPromotion' | 'timeInRole' | 'totalAdjustments';
	title: string;
	value: string;
	subtitle: string;
	icon: string;
	iconTone: 'blue' | 'gray';
}

@Component({
	selector: 'app-employee-details-salary-history',
	templateUrl: './employee-details-salary-history.component.html',
	styleUrls: ['./employee-details-salary-history.component.scss'],
})
export class EmployeeDetailsSalaryHistoryComponent implements OnChanges, OnDestroy {
	@Input() employeeId: string | null | undefined;
	@Input() currentJobTitleName: string | null | undefined;

	isLoading = false;
	hasLoadError = false;
	allItems: JobTitleHistoryModel[] = [];
	filteredItems: JobTitleHistoryModel[] = [];
	pagedItems: JobTitleHistoryModel[] = [];
	summaryCards: SalaryHistorySummaryCard[] = [];
	count = 0;

	queryParams: QueryParams = Object.assign(new QueryParams(), {
		top: 10,
		skip: 0,
		page: 1,
		orderBy: 'movementDate',
		orderByOrder: 'desc' as 'asc' | 'desc',
	});

	currentSort: { prop: string; dir: 'asc' | 'desc' } = {
		prop: 'movementDate',
		dir: 'desc',
	};

	columnFilterVisibility: Record<'movementDate' | 'jobTitleName' | 'costCenterName', boolean> = {
		movementDate: false,
		jobTitleName: false,
		costCenterName: false,
	};

	columnQueries = {
		movementDate: '',
		jobTitleName: '',
		costCenterName: '',
	};

	private readonly destroy$ = new Subject<void>();
	private readonly filterSubject = new Subject<void>();
	private loadSubscription?: Subscription;
	private loadedEmployeeId: string | null = null;

	constructor(
		private readonly jobTitleService: JobTitleService,
		private readonly toastService: ToastService,
	) {
		this.filterSubject
			.pipe(debounceTime(300), takeUntil(this.destroy$))
			.subscribe(() => {
				this.queryParams.skip = 0;
				this.queryParams.page = 1;
				this.applyFiltersAndPaging();
			});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!changes.employeeId && !changes.currentJobTitleName) {
			return;
		}

		const id = this.employeeId;
		if (!id) {
			this.resetState();
			return;
		}

		if (changes.employeeId && id !== this.loadedEmployeeId) {
			this.loadHistory(id);
			return;
		}

		if (changes.currentJobTitleName && this.allItems.length > 0) {
			this.summaryCards = this.buildSummaryCards(this.allItems);
		}
	}

	ngOnDestroy(): void {
		this.loadSubscription?.unsubscribe();
		this.destroy$.next();
		this.destroy$.complete();
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent): void {
		const target = event.target as HTMLElement | null;
		if (target?.closest('.salary-history-column-filter-header')) {
			return;
		}
		this.closeAllColumnFilters();
	}

	toggleSort(prop: string): void {
		const dir =
			this.currentSort.prop === prop && this.currentSort.dir === 'desc'
				? 'asc'
				: 'desc';

		this.currentSort = { prop, dir };
		this.queryParams.orderBy = prop;
		this.queryParams.orderByOrder = dir;
		this.applyFiltersAndPaging();
	}

	sortIcon(prop: string): string {
		if (this.currentSort.prop !== prop) {
			return 'icon-chevron-down';
		}
		return this.currentSort.dir === 'asc' ? 'icon-chevron-up' : 'icon-chevron-down';
	}

	onPaginatorQuery(): void {
		const top = this.queryParams.top || 10;
		const page = this.queryParams.page || 1;
		this.queryParams.skip = top * (page - 1);
		this.applyFiltersAndPaging();
	}

	toggleColumnFilter(column: keyof typeof this.columnFilterVisibility): void {
		const isOpen = this.columnFilterVisibility[column];
		this.columnFilterVisibility = {
			movementDate: false,
			jobTitleName: false,
			costCenterName: false,
			[column]: !isOpen,
		};
	}

	onColumnFilterChange(): void {
		this.filterSubject.next();
	}

	clearColumnFilter(column: keyof typeof this.columnQueries): void {
		this.columnQueries[column] = '';
		this.columnFilterVisibility[column] = false;
		this.filterSubject.next();
	}

	retryLoad(): void {
		if (this.employeeId) {
			this.loadHistory(this.employeeId);
		}
	}

	formatSalary(value: number | null | undefined): string {
		const amount = Number(value ?? 0);
		return amount.toLocaleString('pt-BR', {
			style: 'currency',
			currency: 'BRL',
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});
	}

	formatVariation(value: number | null | undefined): string | null {
		if (value === null || value === undefined || Number.isNaN(Number(value))) {
			return null;
		}
		const amount = Number(value);
		const formatted = this.formatVariationValue(amount);
		if (amount > 0) {
			return `\u2191 ${formatted}`;
		}
		if (amount < 0) {
			return `\u2193 ${formatted}`;
		}
		return formatted;
	}

	formatVariationValue(value: number | null | undefined): string {
		const amount = Number(value ?? 0);
		const formatted = Math.abs(amount).toLocaleString('pt-BR', {
			minimumFractionDigits: Number.isInteger(amount) ? 0 : 1,
			maximumFractionDigits: 2,
		});
		return `${formatted}%`;
	}

	variationClass(value: number | null | undefined): string {
		if (value === null || value === undefined || Number.isNaN(Number(value))) {
			return 'variation-badge--neutral';
		}
		const amount = Number(value);
		if (amount > 0) {
			return 'variation-badge--up';
		}
		if (amount < 0) {
			return 'variation-badge--down';
		}
		return 'variation-badge--neutral';
	}

	private loadHistory(employeeId: string): void {
		this.isLoading = true;
		this.hasLoadError = false;
		this.loadedEmployeeId = employeeId;
		this.loadSubscription?.unsubscribe();

		this.loadSubscription = this.jobTitleService
			.listHistoryByEmployee(employeeId, true)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: items => {
					this.allItems = (items || []).slice().sort((a, b) => this.compareDatesDesc(a.movementDate, b.movementDate));
					this.summaryCards = this.buildSummaryCards(this.allItems);
					this.queryParams.skip = 0;
					this.queryParams.page = 1;
					this.applyFiltersAndPaging();
					this.isLoading = false;
				},
				error: () => {
					this.hasLoadError = true;
					this.isLoading = false;
					this.allItems = [];
					this.filteredItems = [];
					this.pagedItems = [];
					this.summaryCards = this.buildEmptySummaryCards();
					this.count = 0;
					this.toastService.showError(
						'Não foi possível carregar o histórico salarial. Tente novamente.',
						'top-center',
					);
				},
			});
	}

	private applyFiltersAndPaging(): void {
		let rows = [...this.allItems];

		const dateQuery = (this.columnQueries.movementDate || '').trim().toLowerCase();
		if (dateQuery) {
			rows = rows.filter(item =>
				moment(item.movementDate).isValid()
				&& moment(item.movementDate).format('DD/MM/YYYY').toLowerCase().includes(dateQuery)
			);
		}

		const titleQuery = (this.columnQueries.jobTitleName || '').trim().toLowerCase();
		if (titleQuery) {
			rows = rows.filter(item => (item.jobTitleName || '').toLowerCase().includes(titleQuery));
		}

		const costCenterQuery = (this.columnQueries.costCenterName || '').trim().toLowerCase();
		if (costCenterQuery) {
			rows = rows.filter(item => (item.costCenterName || '').toLowerCase().includes(costCenterQuery));
		}

		const prop = this.currentSort.prop || 'movementDate';
		const dir = this.currentSort.dir === 'asc' ? 1 : -1;
		rows.sort((a, b) => {
			const left = this.getSortableValue(a, prop);
			const right = this.getSortableValue(b, prop);
			if (left < right) {
				return -1 * dir;
			}
			if (left > right) {
				return 1 * dir;
			}
			return 0;
		});

		this.filteredItems = rows;
		this.count = rows.length;

		const top = this.queryParams.top || 10;
		const skip = this.queryParams.skip || 0;
		this.pagedItems = rows.slice(skip, skip + top);
	}

	private getSortableValue(item: JobTitleHistoryModel, prop: string): string | number {
		switch (prop) {
			case 'movementDate':
				return moment(item.movementDate).valueOf() || 0;
			case 'changeType':
				return (item.changeType || '').toLowerCase();
			case 'jobTitleName':
				return (item.jobTitleName || '').toLowerCase();
			case 'costCenterName':
				return (item.costCenterName || '').toLowerCase();
			case 'salary':
				return Number(item.salary || 0);
			case 'variation':
				return item.variation == null ? Number.NEGATIVE_INFINITY : Number(item.variation);
			default:
				return '';
		}
	}

	private buildSummaryCards(items: JobTitleHistoryModel[]): SalaryHistorySummaryCard[] {
		if (!items.length) {
			return this.buildEmptySummaryCards();
		}

		const chronological = [...items].sort((a, b) => this.compareDatesAsc(a.movementDate, b.movementDate));
		const latest = chronological[chronological.length - 1];

		const lastPromotion = [...chronological]
			.reverse()
			.find(item => item.isPromotion);

		const timeInRoleFrom = this.resolveTimeInCurrentRoleStart(chronological);
		const timeInRoleLabel = this.formatDurationSince(timeInRoleFrom, 'long');
		const currentRoleName =
			latest?.jobTitleName
			|| this.currentJobTitleName
			|| '—';

		const totalAdjustments = Math.max(items.length - 1, 0);

		return [
			{
				key: 'lastPromotion',
				title: 'Última promoção',
				value: lastPromotion?.movementDate
					? moment(lastPromotion.movementDate).format('DD/MM/YYYY')
					: '—',
				subtitle: lastPromotion?.movementDate
					? `${this.formatDurationSince(lastPromotion.movementDate, 'long')} atrás`
					: 'Sem promoção registrada',
				icon: 'award',
				iconTone: 'blue',
			},
			{
				key: 'timeInRole',
				title: 'Tempo na função atual',
				value: timeInRoleLabel,
				subtitle: currentRoleName,
				icon: 'clock',
				iconTone: 'gray',
			},
			{
				key: 'totalAdjustments',
				title: 'Total de reajustes',
				value: String(totalAdjustments),
				subtitle: 'desde a admissão',
				icon: 'refresh-cw',
				iconTone: 'gray',
			},
		];
	}

	private buildEmptySummaryCards(): SalaryHistorySummaryCard[] {
		return [
			{
				key: 'lastPromotion',
				title: 'Última promoção',
				value: '—',
				subtitle: 'Sem promoção registrada',
				icon: 'award',
				iconTone: 'blue',
			},
			{
				key: 'timeInRole',
				title: 'Tempo na função atual',
				value: '—',
				subtitle: this.currentJobTitleName || '—',
				icon: 'clock',
				iconTone: 'gray',
			},
			{
				key: 'totalAdjustments',
				title: 'Total de reajustes',
				value: '0',
				subtitle: 'desde a admissão',
				icon: 'refresh-cw',
				iconTone: 'gray',
			},
		];
	}

	private resolveTimeInCurrentRoleStart(chronological: JobTitleHistoryModel[]): string | null {
		if (!chronological.length) {
			return null;
		}

		const latest = chronological[chronological.length - 1];
		const currentJobTitleId = latest.jobTitleId || null;
		const currentJobTitleName = (latest.jobTitleName || '').trim().toLowerCase();

		let start = latest.movementDate;
		for (let i = chronological.length - 2; i >= 0; i--) {
			const item = chronological[i];
			const sameId = currentJobTitleId && item.jobTitleId && item.jobTitleId === currentJobTitleId;
			const sameName =
				!!currentJobTitleName
				&& (item.jobTitleName || '').trim().toLowerCase() === currentJobTitleName;

			if (sameId || sameName) {
				start = item.movementDate;
				continue;
			}
			break;
		}

		return start;
	}

	private formatDurationSince(
		dateValue: string | null | undefined,
		style: 'short' | 'long' = 'short',
	): string {
		if (!dateValue || !moment(dateValue).isValid()) {
			return '—';
		}

		const start = moment(dateValue).startOf('day');
		const end = moment().startOf('day');
		if (end.isBefore(start)) {
			return '—';
		}

		const years = end.diff(start, 'years');
		const afterYears = start.clone().add(years, 'years');
		const months = end.diff(afterYears, 'months');

		if (years <= 0 && months <= 0) {
			const days = end.diff(start, 'days');
			if (days <= 0) {
				return 'hoje';
			}
			return days === 1 ? '1 dia' : `${days} dias`;
		}

		if (style === 'long') {
			const yearLabel =
				years <= 0 ? '' : years === 1 ? '1 Ano' : `${years} Anos`;
			const monthLabel =
				months <= 0 ? '' : months === 1 ? '1 Mês' : `${months} Meses`;
			return [yearLabel, monthLabel].filter(Boolean).join(' e ') || '—';
		}

		const yearLabel = years > 0 ? `${years} a` : '';
		const monthLabel = months > 0 ? `${months} m` : '';
		return [yearLabel, monthLabel].filter(Boolean).join(' e ') || '—';
	}

	private compareDatesDesc(left: string, right: string): number {
		return (moment(right).valueOf() || 0) - (moment(left).valueOf() || 0);
	}

	private compareDatesAsc(left: string, right: string): number {
		return (moment(left).valueOf() || 0) - (moment(right).valueOf() || 0);
	}

	private closeAllColumnFilters(): void {
		this.columnFilterVisibility = {
			movementDate: false,
			jobTitleName: false,
			costCenterName: false,
		};
	}

	private resetState(): void {
		this.loadedEmployeeId = null;
		this.isLoading = false;
		this.hasLoadError = false;
		this.allItems = [];
		this.filteredItems = [];
		this.pagedItems = [];
		this.count = 0;
		this.summaryCards = this.buildEmptySummaryCards();
		this.columnQueries = {
			movementDate: '',
			jobTitleName: '',
			costCenterName: '',
		};
		this.closeAllColumnFilters();
	}
}
